import { Button, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import image from '../../../assets/css/images/police.jpg';
import { useEffect, useState } from 'react';
import UsersService from '../../../services/UsersService';
import useUser from '../../../use/useUser';
import TableWithPagining from '../../../components/Table/TableWithPagining';
import PageForm from '../../../components/PageHeader/PageForm';
import { Column } from '../../../interfaces/Interfaces';

export default function Users() {
    const { t } = useTranslation("default");
    const [users, setUsers] = useState<any[]>([]);
    const [count, setCount] = useState(0);
    const [currentUser] = useUser();

    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [reportDays, setReportDays] = useState<number>(7);
    const [sending, setSending] = useState(false);

    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const showSnackbar = (
        message: string,
        severity: 'success' | 'error' | 'warning'
    ) => {
        setSnackbar({ open: true, message, severity });
    };

    const isAdmin = currentUser?.role === 1;

    const openReportDialog = () => {
        if (!isAdmin) {
            showSnackbar(t('default.noPermission'), 'error');
            return;
        }
        setReportDays(7);
        setReportDialogOpen(true);
    };

    const closeReportDialog = () => {
        if (sending) return;
        setReportDialogOpen(false);
    };

    const confirmSendReport = async () => {
        if (sending) return;

        if (reportDays < 1) {
            showSnackbar(t('default.invalidDays') || 'Invalid number of days', 'warning');
            return;
        }

        setReportDialogOpen(false);
        setSending(true);

        try {
            await UsersService.sendWeeklyReportNow(reportDays);
            showSnackbar(t('default.sendSuccess'), 'success');
        } catch (err) {
            console.error(err);
            showSnackbar(t('default.sendFailed'), 'error');
        } finally {
            setSending(false);
        }
    };


    const columns: Column[] = [
        { id: 'id', label: 'Id', minWidth: 170 },
        { id: 'name', label: t('default.name'), minWidth: 170 },
        { id: 'surname', label: t('default.surname'), minWidth: 170 },
        { id: 'email', label: t('default.email'), minWidth: 170 },
        {
            id: 'role',
            label: t('default.role'),
            minWidth: 170,
            align: 'center',
            format: (value: number) =>
                value === 1 ? t('default.admin') : t('default.user')
        },
        {
            id: 'wants_newsletter',
            label: t('default.weeklyReport'),
            minWidth: 120,
            align: 'center',
            format: (value: any, row: any) => (
                <Checkbox
                    checked={Boolean(row?.wants_newsletter)}
                    onChange={() => toggleNewsletterForUser(row.id)}
                    size="small"
                />
            )
        }
    ];

    const loadData = (page: number, perPage: number) => {
        UsersService.getUsers("", page, perPage)
            .then((resp: any) => {
                setUsers(resp.data.data);
                setCount(resp.data.totalCount);
            })
            .catch(() => { });
    };

    useEffect(() => {
        loadData(0, 10);
    }, []);

    const changeRole = (idx: number) => {
        const role = users[idx].role === 1 ? 2 : 1;
        UsersService.changeRole(users[idx].id, role)
            .then(() => {
                const tempUsers = [...users];
                tempUsers[idx].role = role;
                setUsers(tempUsers);
            })
            .catch(() => { });
    };

    const actButt = [
        {
            element: <Button>{t('default.changeRole')}</Button>,
            action: changeRole
        }
    ];

    const toggleNewsletterForUser = (userId: number | string) => {
        setUsers(prev => {
            const idx = prev.findIndex(u => u.id === userId);
            if (idx === -1) return prev;

            const updated = [...prev];
            updated[idx] = {
                ...updated[idx],
                wants_newsletter: !updated[idx].wants_newsletter
            };
            return updated;
        });

        const user = users.find(u => u.id === userId);
        if (!user) return;

        const newVal = !user.wants_newsletter;

        UsersService.setNewsletterForUser(user.id, newVal).catch(() => {
            setUsers(prev => {
                const idx = prev.findIndex(u => u.id === userId);
                if (idx === -1) return prev;

                const reverted = [...prev];
                reverted[idx] = {
                    ...reverted[idx],
                    wants_newsletter: !newVal
                };
                return reverted;
            });
        });
    };

    return (
        <PageForm label={t("menu.userManagement")} image={image}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <Button color="primary"  variant="contained"  onClick={openReportDialog} disabled={!isAdmin}>
                    {t('default.sendWeeklyReport')}
                </Button>
            </div>

            <TableWithPagining columns={columns as any} rows={users} totalCount={count} actions={actButt} loadData={loadData}/>

            <Dialog open={reportDialogOpen} onClose={closeReportDialog} maxWidth="xs" fullWidth>
                <DialogTitle>{t('default.sendWeeklyReport')}</DialogTitle>
                <DialogContent>
                    <TextField autoFocus margin="dense" fullWidth label={t('default.daysLabel') || 'Days'} type="number"
                        value={reportDays}
                        onChange={(e) => setReportDays(Number(e.target.value))}
                        inputProps={{ min: 1 }}
                        disabled={sending}
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={closeReportDialog} disabled={sending}>
                        {t('default.cancel') || 'Cancel'}
                    </Button>
                    <Button onClick={confirmSendReport} disabled={sending} color="primary" variant="contained" startIcon={sending ? <CircularProgress size={16} /> : null}>
                        {sending
                            ? t('default.sending') || 'Sending…'
                            : t('default.send') || 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() =>
                    setSnackbar(prev => ({ ...prev, open: false }))
                }
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() =>
                        setSnackbar(prev => ({ ...prev, open: false }))
                    }
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </PageForm>
    );
}
